// pkg191 — sessions that survive the day (D-S3/D-S4/D-S5): storage + refresh moved to utils/adminSession.js (single-flight); the 7h timer becomes a backup behind refresh-on-wake (visibilitychange/focus → refresh when the 8h JWT has <1h left, so a slept laptop no longer wakes to a dead page); lm:session-expired (a terminal refresh failure anywhere, incl. adminApi's retry) signs the user out; the Stay-logged-in choice is persisted for the login page's default — delivered 2026-10-31
// pkg20a — master key login removed; Bearer JWT is the only admin auth — delivered 2026-06-10
// Per-user admin authentication context.
//
// Tracks the currently signed-in admin user (or null if signed out) and
// exposes login/logout helpers.
//
// Storage strategy:
//   - "Stay logged in" checked  → access token in localStorage, refresh token
//     in localStorage (ryanlaw_admin_refresh). The refresh token is exchanged
//     silently for a new access token before it expires (every 7h).
//   - "Stay logged in" NOT checked → access token in sessionStorage only.
//     Session ends when the tab/window is closed.
//
// Signing in is JWT-only (email + password). The legacy master-key login was
// removed in pkg20a; `adminUser.isMasterKey` is always false now and survives
// only as an inert field read by older UI code.

import React, {
  createContext, useContext, useState, useEffect, useCallback, useRef,
} from 'react';

import {
  LS_REFRESH, LS_REMEMBER_PREF, SESSION_EXPIRED_EVENT,
  getStoredToken, setStoredToken, clearStoredToken, clearSession,
  refreshAccessToken, ensureFreshToken,
} from '../utils/adminSession';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// pkg191 — storage keys + helpers live in utils/adminSession.js now (shared
// with adminApi's 403 → refresh → retry). Same keys as before; nothing an
// existing browser holds is invalidated by this deploy.
const BACKUP_TIMER_MS = 7 * 60 * 60 * 1000;   // D-S4: the old 7h tick, now a backup
const WAKE_THRESHOLD_SEC = 60 * 60;           // D-S4: refresh on wake when <1h left

// ─────────────────────────────────────────────────────────────────────────────

const AdminAuthContext = createContext({
  adminUser: null,
  loading: true,
  loginWithCredentials: async () => {},
  logout: async () => {},
  refresh: () => {},
});

export function useAdminAuth() {
  return useContext(AdminAuthContext);
}

export function AdminAuthProvider({ children }) {
  const [adminUser, setAdminUser] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const refreshTimerRef = useRef(null);

  // ── Silent token refresh ─────────────────────────────────────────────────
  // Called once after a successful login with rememberMe, and once on startup
  // if a refresh token is found. Schedules itself to re-run at 7h intervals
  // so the 8h access token never expires while the browser is open.

  // pkg191: the timer is a backup. refreshAccessToken() is single-flight and
  // handles terminal failure itself (clears storage, fires lm:session-expired,
  // which the listener below turns into setAdminUser(null)). A transient
  // failure (5xx, offline) keeps the tokens; the next wake or tick retries.
  const scheduleRefresh = useCallback((delayMs) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(async () => {
      if (!localStorage.getItem(LS_REFRESH)) return;
      await refreshAccessToken();
      if (localStorage.getItem(LS_REFRESH)) scheduleRefresh(BACKUP_TIMER_MS);
    }, delayMs);
  }, []);

  // pkg191 (D-S4): refresh on wake. Background tabs get their timers
  // throttled and sleeping laptops skip them entirely; the moment the tab is
  // visible/focused again, top the token up if it's close to expiry.
  useEffect(() => {
    const onWake = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      if (!localStorage.getItem(LS_REFRESH)) return;
      ensureFreshToken(WAKE_THRESHOLD_SEC);
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, []);

  // pkg191 (D-S3): a terminal refresh failure anywhere (startup, timer, wake,
  // or adminApi's retry path) ends the session here, once.
  useEffect(() => {
    const onExpired = () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      setAdminUser(null);
      setLoading(false);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  // ── Startup: validate existing credentials ───────────────────────────────
  //
  // Priority order:
  //   1. If a refresh token exists in localStorage, always use it to get a
  //      fresh access token. This covers the "browser closed and reopened"
  //      case where sessionStorage is wiped but localStorage persists — we
  //      skip trusting any potentially-expired stored access token and go
  //      straight to a guaranteed-fresh exchange.
  //   2. If only an access token exists (non-persistent session), validate it.
  //   3. Otherwise not logged in.

  const refresh = useCallback(async () => {
    const rawRefresh = localStorage.getItem(LS_REFRESH);

    // ── Path 1: refresh token present — always prefer this ─────────────────
    if (rawRefresh) {
      try {
        // pkg191: single-flight rotation. On a terminal failure it has already
        // cleared storage and fired lm:session-expired; we just fall through.
        // On a transient failure (offline) fall back to the stored access
        // token if it still has life — don't log the user out for a blip.
        const token = (await refreshAccessToken()) || getStoredToken();
        if (!token) throw new Error('expired');
        const meRes = await fetch(`${BASE}/api/auth/admin-me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!meRes.ok) throw new Error('me failed');
        const meData = await meRes.json();
        setAdminUser(meData);
        scheduleRefresh(BACKUP_TIMER_MS);
      } catch {
        clearSession();
        setAdminUser(null);
      }
      setLoading(false);
      return;
    }

    // ── Path 2: session-only access token ──────────────────────────────────
    const token = getStoredToken();
    if (!token) {
      setAdminUser(null);
      setLoading(false);
      return;
    }

    const headers = { Authorization: `Bearer ${token}` };

    try {
      const res = await fetch(`${BASE}/api/auth/admin-me`, { headers });
      if (!res.ok) throw new Error('not authenticated');
      const data = await res.json();
      setAdminUser(data);
    } catch (err) {
      clearStoredToken();
      setAdminUser(null);
    } finally {
      setLoading(false);
    }
  }, [scheduleRefresh]);

  useEffect(() => {
    refresh();
    return () => { if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current); };
  }, [refresh]);

  // ── Login ────────────────────────────────────────────────────────────────

  async function loginWithCredentials(email, password, rememberMe = false) {
    const res = await fetch(`${BASE}/api/auth/admin-login`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ email, password, rememberMe }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Invalid credentials');

    // Sweep any legacy master key left in storage from before pkg20a.
    localStorage.removeItem('ryanlaw_admin_key');

    setStoredToken(data.token, rememberMe);
    try { localStorage.setItem(LS_REMEMBER_PREF, rememberMe ? '1' : '0'); } catch { /* ignore */ } // pkg191 (D-S5)

    if (rememberMe && data.refreshToken) {
      localStorage.setItem(LS_REFRESH, data.refreshToken);
      scheduleRefresh(BACKUP_TIMER_MS);
    } else {
      localStorage.removeItem(LS_REFRESH);
    }

    setAdminUser(data.adminUser);
    return data.adminUser;
  }


  // ── Logout ───────────────────────────────────────────────────────────────

  async function logout() {
    const raw = localStorage.getItem(LS_REFRESH);
    try {
      if (raw) {
        await fetch(`${BASE}/api/auth/admin-logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: raw }),
        });
      }
    } catch { /* best-effort — always clear locally */ }

    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    clearSession();
    localStorage.removeItem('ryanlaw_admin_key'); // legacy master-key sweep
    setAdminUser(null);
  }

  return (
    <AdminAuthContext.Provider value={{
      adminUser, loading,
      loginWithCredentials, logout, refresh,
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

// Convenience: returns the role-specific portal label that goes in the
// browser tab and sidebar. Maps:
//   owner    → 'Admin Panel'
//   attorney → 'Attorney Portal'
//   staff    → 'Staff Portal'
//   master   → 'Admin Panel' (we don't know the human's role)
//   none     → null
export function useAdminPortalLabel() {
  const { adminUser } = useAdminAuth();
  if (!adminUser) return null;
  if (adminUser.isMasterKey) return 'Admin Panel';
  if (adminUser.role === 'attorney') return 'Attorney Portal';
  if (adminUser.role === 'staff')    return 'Staff Portal';
  return 'Admin Panel';
}
